/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import toast from "react-hot-toast";

import capitalize from "utils/capitalize";
import { sentryCaptureError } from "utils/errorFormatter";
import isObject from "utils/isObject";
import openToast from "utils/openToast";
import warning from "utils/warning";

class FetchError {
  constructor(res, options) {
    const settings = {
      message: "",
      alertError: true,
      toastType: "error", // error || info
      shapeErrorMessage: null,
    };

    if (options) {
      if (typeof options === "string") {
        settings.message = options;
      }

      if (typeof options === "object") {
        Object.assign(settings, options);
      }
    }

    const { message, alertError, toastType, shapeErrorMessage } = settings;
    let t;
    const isAbortedRequest = res === "ABORTED";
    if (message && message.length > 0 && !isAbortedRequest) {
      if (toastType === "info") {
        t = openToast(message);
      } else {
        t = openToast(message, "error");
      }
    }

    const showErrorToast = (msg) => {
      toast.dismiss(t);
      openToast(msg, "error");
    };
    // no response, means it is an offline custom error.
    if (!res) {
      this.data = null;
      this.ok = false;
      this.status = "FAILED";
      this.statusCode = 400;
      this.error = "Bad request";
      this.response = null;
      sentryCaptureError(this, "Bad request");
      return this;
    }

    if (isAbortedRequest) {
      this.data = null;
      this.ok = false;
      this.status = "FAILED";
      this.statusCode = 0;
      this.error = "ABORTED";
      this.response = null;
      sentryCaptureError(this, "Aborted request");
      return this;
    }

    if (res.status === 401) {
      return Promise.resolve(res.clone().json()).then((error) => {
        let errorMessage =
          "Unauthorized request denied. Please contact support.";

        if (error.hasOwnProperty("message")) {
          errorMessage = error.message;
        } else if (error.hasOwnProperty("error")) {
          errorMessage = error.error;
        }

        if (error.hasOwnProperty("is_expired")) {
          window.location.href = "/";
          localStorage.setItem("is_expired", "true");
        }

        if (alertError) {
          showErrorToast(errorMessage);
        }

        this.data = null;
        this.ok = false;
        this.status = "FAILED";
        this.statusCode = res.status;
        this.error = errorMessage;
        this.response = res;
        sentryCaptureError(this, "Unauthorized request denied");
        return this;
      });
    } else if (res.status === 422) {
      return Promise.resolve(res.clone().json()).then((resolved) => {
        const findErrorScope = (response) => {
          const KEYWORDS = ["errors", "error", "message"];
          const responseKeys = Object.keys(response);

          const idx = responseKeys.findIndex((key) => KEYWORDS.includes(key));
          let scopeKey;
          let scope;
          const isKeyword = KEYWORDS.includes(responseKeys[idx]);
          if (isKeyword) {
            scopeKey = responseKeys[idx];
            scope = response[scopeKey];

            if (Array.isArray(scope)) {
              /*
                {
                  response: {
                    errors: ["Name Missing"],
                  },
                  expected: {
                    key: undefined,
                    message: "Name Missing",
                  },
                }
              */
              scopeKey = undefined;
              scope = scope[0];
            } else if (isObject(scope)) {
              /*
                {
                  response: {
                    errors: {
                      name: ["Missing"],
                    },
                  },
                  expected: {
                    key: "name",
                    message: "Missing",
                  },
                }
              */
              const outerScope = { ...scope };
              scopeKey = Object.keys(outerScope)[0];
              scope = outerScope[scopeKey];
            } else if (typeof scope === "string") {
              /* 
                {
                  response: {
                    error: "Name Missing",
                  },
                  expected: {
                    key: undefined,
                    message: "Name Missing",
                  },
                }
              */
              scopeKey = undefined;
            }
          } else {
            /*
            {
              response: {
                name: ["Missing"],
              },
              expected: {
                key: "name",
                message: "Missing",
              },
            }
            */
            scopeKey = responseKeys[0];
            scope = response[scopeKey];
          }

          return {
            scope,
            key: scopeKey,
          };
        };

        const getScopeMessage = (scope) => {
          if (Array.isArray(scope)) {
            return scope[0];
          }

          return scope;
        };

        const getErrorMessage = (server_response) => {
          if (isObject(server_response)) {
            const { scope, key } = findErrorScope(server_response);
            const message = getScopeMessage(scope);
            return {
              message,
              key,
            };
          } else {
            return {
              message: "Something went wrong. Please contact support.",
              key: undefined,
            };
          }
        };

        const { key, message } = getErrorMessage(resolved);
        let toastMessage = message;
        if (key) {
          if (message) {
            toastMessage = `${capitalize(key)}: ${message}`;
          } else {
            toastMessage = `${capitalize(key)}`;
          }
        }

        if (shapeErrorMessage) {
          toastMessage = shapeErrorMessage(toastMessage);
        }

        showErrorToast(toastMessage);
        this.data = null;
        this.ok = false;
        this.status = "FAILED";
        this.statusCode = res.status;
        this.error = message;
        this.response = res;
        sentryCaptureError(this, "Something went wrong with the request");
        return this;
      });
    } else if (res.status === 403) {
      const errorMessage =
        "Not allowed to access this event. Please contact support";

      if (alertError) {
        localStorage.setItem("isCanceledItem", "true");
        showErrorToast(errorMessage);
      }

      this.data = null;
      this.ok = false;
      this.status = "FAILED";
      this.statusCode = res.status;
      this.error = errorMessage;
      this.response = res;
      sentryCaptureError(this, "Not allowed to access this event");
      return this;
    } else if (res.status === 404) {
      const errorMessage =
        "We couldn't find the page or content you're looking for. Please check the URL or try again later.";

      if (alertError) {
        showErrorToast(errorMessage);
      }

      this.data = null;
      this.ok = false;
      this.status = "FAILED";
      this.statusCode = res.status;
      this.error = errorMessage;
      this.response = res;
      sentryCaptureError(this, "Page not found");
      return this;
    } else if (res.status === 500) {
      const errorMessage = "Internal Server Error, please contact support.";
      if (alertError) {
        showErrorToast(errorMessage);
      }

      this.data = null;
      this.ok = false;
      this.status = "FAILED";
      this.statusCode = res.status;
      this.error = errorMessage;
      this.response = res;
      sentryCaptureError(this, "Something wrong happened");
      return this;
    } else {
      warning(res);
      const errorMessage = "Something wrong happened, please contact support.";
      if (alertError) {
        showErrorToast(errorMessage);
      }

      this.data = null;
      this.ok = false;
      this.status = "FAILED";
      this.statusCode = res.status || "UNKNOWN";
      this.error = errorMessage;
      this.response = res;
      sentryCaptureError(this, "Unknown error");
      return this;
    }
  }
}

export default FetchError;
